/**
 * WidgetPresetGallery — the studio "Cover Presets" / "Landing Presets" tabs.
 *
 * COVER presets are now the unified JSON-cover scheme: each preset is a plain
 * `cover_json` document (SoT = ./covers/presets/*.json) rendered by the shared
 * interpreter (coverRender.buildCoverHtml). Click a tile → the right pane shows
 * its attributes + raw JSON, so a "preset" is literally a JSON doc you can copy,
 * adopt on a widget, and tweak. Landing keeps the legacy single-default gallery.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import WidgetStudioTabs from './WidgetStudioTabs';
import { buildCoverHtml } from './covers/coverRender';
import { buildDefaultLandingHtml, LANDING_PRESETS, EXAMPLE_LANDING } from './landingDefault';

const COLORS = { bg: '#f6f6f9', text: '#1c1c1e', textDim: '#6e6e73', accent: '#6E59F2', bgCard: '#fff', border: '#e2e2ea' };

// All cover_json presets (the single source of truth). import.meta.glob picks up
// every file in covers/presets/ so adding a preset = dropping a JSON file.
const PRESET_MODULES = import.meta.glob('./covers/presets/*.json', { eager: true });
const COVER_PRESETS = Object.values(PRESET_MODULES)
  .map((m) => m.default || m)
  .filter((p) => p && p.cover_json)
  .sort((a, b) => String(a.label || a.id).localeCompare(String(b.label || b.id)));

// Sample feed so the data-wired presets render richly: images + calories so
// count/sum/recent/latest + image binds all populate. (calories sum = 1800.)
const SAMPLE_COVER_DATA = {
  title: 'Sample Folder',
  eyebrow: 'Folder',
  entries: [
    { payload: { name: 'Monstera', calories: 320, images: ['https://images.unsplash.com/photo-1614594975525-e45190c55d0b?w=400&q=80'] } },
    { payload: { name: 'Fiddle Leaf Fig', calories: 210, images: ['https://images.unsplash.com/photo-1453904300235-0f2f60b15b5d?w=400&q=80'] } },
    { payload: { name: 'Pothos', calories: 640, images: ['https://images.unsplash.com/photo-1572688484438-313a6e50c333?w=400&q=80'] } },
    { payload: { name: 'Snake Plant', calories: 180, images: ['https://images.unsplash.com/photo-1593691509543-c55fb32d8de5?w=400&q=80'] } },
    { payload: { name: 'Aloe Vera', calories: 300 } }, { payload: { name: 'Cactus', calories: 150 } },
    { payload: { name: 'Boston Fern' } }, { payload: { name: 'Orchid' } }, { payload: { name: 'English Ivy' } },
    { payload: { name: 'Areca Palm' } }, { payload: { name: 'Basil' } }, { payload: { name: 'Mint' } },
  ],
  stat_value: 4, stat_label: 'with photo',
};

function buildBlobUrl(html, widgetData) {
  const inject = `<script>window.__WIDGET_DATA__=${JSON.stringify(widgetData)};window.__USER_DATA__=${JSON.stringify({ appearance: 'auto' })};</script>`;
  const headIdx = html.search(/<head[^>]*>/i);
  let merged;
  if (headIdx !== -1) { const after = html.indexOf('>', headIdx) + 1; merged = html.slice(0, after) + inject + html.slice(after); }
  else merged = inject + html;
  return URL.createObjectURL(new Blob([merged], { type: 'text/html; charset=utf-8' }));
}

// A human summary of a preset's data wiring (binds/aggs found in its nodes).
function summarizeWiring(coverJson) {
  const out = [];
  const walk = (node, label) => {
    if (!node || typeof node !== 'object') return;
    if (typeof node.bind === 'string') out.push(`${label} ← ${node.bind}`);
    if (node.agg && node.agg.op) out.push(`${label} ← ${node.agg.op}(${node.agg.of || 'entries'}${node.agg.field ? '.' + node.agg.field : ''})`);
  };
  const bg = coverJson.bg;
  if (bg && bg.image) walk(bg.image, 'bg.image');
  for (const n of coverJson.nodes || []) {
    walk(n, n.type);
    if (n.ref) walk(n.ref, `${n.type}.ref`);
    if (n.refs) walk(n.refs, `${n.type}.refs`);
    if (n.sub) walk(n.sub, `${n.type}.sub`);
  }
  return out;
}

function CoverGallery() {
  const [tiles, setTiles] = useState([]);
  const [selectedId, setSelectedId] = useState(COVER_PRESETS[0]?.id || null);
  const urlsRef = useRef([]);

  useEffect(() => {
    urlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* noop */ } });
    urlsRef.current = [];
    const built = COVER_PRESETS.map((p) => {
      // static {file} refs (the app's real cover art) are served from public/cover-presets/.
      // Use ABSOLUTE URLs: the cover renders in a blob: iframe where a root-relative
      // "/cover-presets/…" has no base origin and won't load.
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const html = buildCoverHtml(p.cover_json, { resolveFileUrl: (n) => `${origin}/cover-presets/${n}` });
      // each preset carries its own preview `example` (like a widget's example_data);
      // fall back to the generic sample so a preset without one still renders. Example
      // image paths use the same /cover-presets/ convention → absolutize them too.
      const raw = p.example || { ...SAMPLE_COVER_DATA, title: p.label || SAMPLE_COVER_DATA.title };
      const previewData = JSON.parse(JSON.stringify(raw).replaceAll('/cover-presets/', `${origin}/cover-presets/`));
      const u = buildBlobUrl(html, previewData);
      urlsRef.current.push(u);
      const size = p.cover_json.size || { w: 240, h: 170 };
      return { id: p.id, label: p.label, url: u, w: size.w, h: size.h };
    });
    setTiles(built);
    return () => {
      urlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* noop */ } });
      urlsRef.current = [];
    };
  }, []);

  const selected = useMemo(() => COVER_PRESETS.find((p) => p.id === selectedId) || COVER_PRESETS[0], [selectedId]);
  const wiring = useMemo(() => (selected ? summarizeWiring(selected.cover_json) : []), [selected]);

  // scale tiles down so tall ones fit the grid (cap the rendered width).
  const scaleFor = (w) => Math.min(1, 190 / w);

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', minHeight: 0, flex: 1 }}>
      {/* left: tile grid */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
          {tiles.map((t) => {
            const s = scaleFor(t.w);
            const on = t.id === selectedId;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8, padding: 0, border: 'none',
                  background: 'transparent', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{
                  width: t.w * s, height: t.h * s, borderRadius: 14, overflow: 'hidden', background: '#F2F2F7',
                  boxShadow: on ? `0 0 0 3px ${COLORS.accent}, 0 8px 22px rgba(0,0,0,0.16)` : '0 6px 18px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.08)',
                  border: `1px solid ${COLORS.border}`, transition: 'box-shadow .15s',
                }}>
                  <iframe
                    src={t.url}
                    title={`preset-${t.id}`}
                    // allow-same-origin so the cover can load the app's SF Pro Display OTF
                    // (CORS blocks a font from an opaque-origin blob). Safe here: the gallery
                    // only ever renders our OWN trusted cover_json presets, never user HTML.
                    sandbox="allow-scripts allow-same-origin"
                    scrolling="no"
                    style={{ width: t.w, height: t.h, transform: `scale(${s})`, transformOrigin: 'top left', border: 0, display: 'block', pointerEvents: 'none' }}
                  />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: on ? COLORS.accent : COLORS.text }}>
                  {t.label} <span style={{ color: COLORS.textDim, fontWeight: 400 }}>· {t.id}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* right: inspector */}
      {selected && (
        <aside style={{
          width: 380, flex: '0 0 380px', position: 'sticky', top: 0, alignSelf: 'flex-start',
          background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 18,
          display: 'flex', flexDirection: 'column', gap: 14, maxHeight: 'calc(100vh - 180px)',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{selected.label}</div>
            <div style={{ fontSize: 12, color: COLORS.textDim }}>id: <code>{selected.id}</code> · {selected.cover_json.size?.w}×{selected.cover_json.size?.h} · {(selected.cover_json.nodes || []).length} nodes</div>
          </div>
          {selected.description && <p style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.5, margin: 0 }}>{selected.description}</p>}
          {wiring.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: COLORS.textDim, marginBottom: 6 }}>Data wiring</div>
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {wiring.map((w, i) => (
                  <li key={i} style={{ fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: COLORS.text }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: COLORS.textDim }}>Raw cover_json</div>
              <button
                onClick={() => { try { navigator.clipboard?.writeText(JSON.stringify(selected.cover_json, null, 2)); } catch { /* noop */ } }}
                style={{ fontSize: 11, fontWeight: 600, color: COLORS.accent, background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '3px 10px', cursor: 'pointer' }}
              >Copy</button>
            </div>
            <pre style={{
              margin: 0, flex: 1, overflow: 'auto', background: '#0f0f14', color: '#e6e6ea', borderRadius: 10,
              padding: 12, fontSize: 11.5, lineHeight: 1.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}>{JSON.stringify(selected.cover_json, null, 2)}</pre>
          </div>
        </aside>
      )}
    </div>
  );
}

function LandingGallery() {
  const [url, setUrl] = useState(null);
  const urlRef = useRef(null);
  useEffect(() => {
    if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch { /* noop */ } }
    const u = buildBlobUrl(buildDefaultLandingHtml(EXAMPLE_LANDING, { appearance: 'auto' }), {});
    urlRef.current = u;
    setUrl(u);
    return () => { if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch { /* noop */ } } };
  }, []);
  const p = LANDING_PRESETS[0] || { id: 'default', label: 'Default' };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
      {url && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ width: 300, height: 534, borderRadius: 14, overflow: 'hidden', background: '#F2F2F7', boxShadow: '0 6px 18px rgba(0,0,0,0.10)', border: `1px solid ${COLORS.border}` }}>
            <iframe src={url} title={`landing-${p.id}`} sandbox="allow-scripts" scrolling="no" style={{ width: '100%', height: '100%', border: 0, display: 'block', pointerEvents: 'none' }} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{p.label} <span style={{ color: COLORS.textDim, fontWeight: 400 }}>· {p.id}</span></div>
        </div>
      )}
    </div>
  );
}

export default function WidgetPresetGallery({ kind }) { // 'cover' | 'landing'
  const isCover = kind === 'cover';
  return (
    <div style={{
      padding: '24px 32px', height: '100%', minHeight: 0, overflowY: 'auto',
      background: COLORS.bg, color: COLORS.text, display: 'flex', flexDirection: 'column', gap: 20,
    }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <WidgetStudioTabs active={isCover ? 'cover-presets' : 'landing-presets'} colors={COLORS} />
      </header>
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>{isCover ? 'Cover Presets' : 'Landing Presets'}</h2>
        <p style={{ fontSize: 13, color: COLORS.textDim, margin: '0 0 20px', maxWidth: 720, lineHeight: 1.5 }}>
          {isCover
            ? 'The unified JSON-cover scheme. Each preset is a cover_json document rendered by the shared interpreter (web + app) — click a tile to see its attributes + raw JSON. A widget adopts one by putting its cover_json in widget.json; each is wired to the widget’s data. Shown here with sample data.'
            : 'The host-rendered landing page. One default for now — the design team adds more. Shown here with sample data.'}
        </p>
      </div>
      {isCover ? <CoverGallery /> : <LandingGallery />}
    </div>
  );
}
